// Keep the current editor mounted until the original account is authenticated again.
// Never replay writes: the user reviews and submits again after recovery.
export type SessionProblem = 'expired' | 'different_account' | 'connection' | null;
let owner: number | null = null;
let problem: SessionProblem = null;
let checking: Promise<boolean> | null = null;
const listeners = new Set<() => void>();
export const sessionProblem = () => problem;
export const subscribeSession = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
function publish(next: SessionProblem) {
  if (next === problem) return;
  problem = next; listeners.forEach(listener => listener());
}
export function setSessionOwner(id: number | null) {
  owner = id; publish(null);
}
export function observeSessionError(error: any) {
  if (!owner) return;
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (status === 401 || (status === 403 && detail === 'Authentication credentials were not provided.')) publish('expired');
}
export async function checkSession(): Promise<boolean> {
  if (!owner) return true;
  if (checking) return checking;
  const expected = owner;
  checking = (async () => {
    try {
      const response = await fetch('/api/auth/me/', { credentials: 'same-origin', cache: 'no-store' });
      if (expected !== owner) return false;
      if (response.status === 401 || response.status === 403) { publish('expired'); return false; }
      if (!response.ok) { publish('connection'); return false; }
      const user = await response.json();
      if (expected !== owner) return false;
      if (user.id !== expected) { publish('different_account'); return false; }
      publish(null); return true;
    } catch { if (expected === owner) publish('connection'); return false; }
    finally { checking = null; }
  })();
  return checking;
}
export async function guardSessionRequest(config: any) {
  if (!owner || /^\/?api\/(auth\/|csrf\/)/.test(config.url || '')) return config;
  const write = !['get', 'head', 'options'].includes((config.method || 'get').toLowerCase());
  // Validate identity before writes, including after another tab changes accounts.
  if (problem || (write && !(await checkSession()))) {
    throw new Error('Your work has not been submitted. Restore your session, then try again.');
  }
  return config;
}
